import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { apiClient } from '@/services/api-client';
import type {
    SpeciesCatalogEntry,
    SpeciesStats,
    SpeciesRelatedEntry,
    CromosAlbumEntry,
    SpeciesRelationType,
} from '../types';

export type { SpeciesRelationType, CromosAlbumRarity } from '../types';

// ─── Lista de especies ──────────────────────────────────────────────────────

export function useSpeciesList(params: {
    search?: string;
    waterType?: string | null;
    rarity?: string | null;
    includeInactive?: boolean;
    needsReviewOnly?: boolean;
    inAlbumOnly?: boolean;
    missingDetails?: boolean;
    missingImage?: boolean;
    limit?: number;
    offset?: number;
}) {
    const {
        search,
        waterType,
        rarity,
        includeInactive = false,
        needsReviewOnly,
        inAlbumOnly,
        missingDetails = false,
        missingImage = false,
        limit = 100,
        offset = 0,
    } = params;
    return useQuery<SpeciesCatalogEntry[]>({
        queryKey: [
            'admin', 'species', 'list',
            search, waterType, rarity, includeInactive,
            needsReviewOnly, inAlbumOnly, missingDetails, missingImage,
            limit, offset,
        ],
        queryFn: async () => {
            const rpcParams: Record<string, unknown> = { p_limit: limit, p_offset: offset };
            if (search?.trim()) rpcParams.p_search = search.trim();
            if (waterType && waterType !== 'all') rpcParams.p_water_type = waterType;
            if (rarity && rarity !== 'all') rpcParams.p_rarity = rarity;
            if (includeInactive) rpcParams.p_include_inactive = true;
            if (needsReviewOnly) rpcParams.p_needs_review_only = true;
            if (inAlbumOnly) rpcParams.p_in_album_only = true;
            if (missingDetails) rpcParams.p_missing_details = true;
            if (missingImage) rpcParams.p_missing_image = true;
            const { data, error } = await dbClient.rpc('admin_get_species_list', rpcParams);
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data) as SpeciesCatalogEntry[];
        },
        staleTime: 30_000,
    });
}

// ─── Especie por ID ─────────────────────────────────────────────────────────

export function useSpeciesById(id: number | null) {
    return useQuery<SpeciesCatalogEntry | null>({
        queryKey: ['admin', 'species', 'detail', id],
        queryFn: async () => {
            if (id == null) return null;
            const { data, error } = await dbClient.rpc('admin_get_species_by_id', { p_id: id });
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            return (rows[0] as SpeciesCatalogEntry) ?? null;
        },
        enabled: id != null && Number.isInteger(id) && id > 0,
        staleTime: 30_000,
    });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export function useCreateSpecies() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: Record<string, unknown>) => {
            const { data, error } = await dbClient.rpc('admin_create_species', params);
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            const row = rows[0] as { id?: number; success?: boolean; message?: string };
            if (!row?.success && row?.success !== undefined) {
                throw new Error(row.message || 'Error al crear especie');
            }
            return row;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'species'] });
        },
    });
}

export function useUpdateSpecies() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: Record<string, unknown>) => {
            const { data, error } = await dbClient.rpc('admin_update_species', params);
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            const row = rows[0] as { success?: boolean; message?: string };
            if (!row?.success && row?.success !== undefined) {
                throw new Error(row.message || 'Error al actualizar especie');
            }
            return row;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'species'] });
        },
    });
}

export function useSoftDeleteSpecies() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data, error } = await dbClient.rpc('admin_soft_delete_species', { p_id: id });
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            const row = rows[0] as { success?: boolean; message?: string };
            if (!row?.success && row?.success !== undefined) {
                throw new Error(row.message || 'Error al desactivar especie');
            }
            return row;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'species'] });
        },
    });
}

export function useRestoreSpecies() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data, error } = await dbClient.rpc('admin_restore_species', { p_id: id });
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            const row = rows[0] as { success?: boolean; message?: string };
            if (!row?.success && row?.success !== undefined) {
                throw new Error(row.message || 'Error al reactivar especie');
            }
            return row;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'species'] });
        },
    });
}

// ─── Upload imagen especie ─────────────────────────────────────────────────

const SPECIES_IMAGES_BASE = 'https://aipecesprost.blob.core.windows.net/species-images/';

/** Genera nombre de archivo normalizado para especie: Genus_species.png */
export function normalizeSpeciesImageFilename(scientificName: string, ext = 'png'): string {
    return scientificName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '') + '.' + ext;
}

/** Sube imagen a species-images. Path debe ser el nombre del archivo (ej. Thunnus_thynnus.png) */
export function useUploadSpeciesImage() {
    return useMutation({
        mutationFn: async ({ path, file }: { path: string; file: File }) => {
            const result = await apiClient.uploadFile('species-images', path, file);
            if (result.error) throw new Error(result.error.message);
            const url = result.data?.publicUrl || result.data?.url;
            if (!url) throw new Error('No se obtuvo URL de la imagen');
            return url;
        },
    });
}

// ─── Species Details (Fase 2: Características) ───────────────────────────────

export interface SpeciesDetailsRow {
    species_catalog_id: number;
    description_es: string | null;
    description_en: string | null;
    extended_info: Record<string, unknown>;
    created_at?: string;
    updated_at?: string;
    seo_title?: string | null;
    seo_description?: string | null;
    seo_faq?: unknown;
}

export function useSpeciesDetails(speciesCatalogId: number | null) {
    return useQuery<SpeciesDetailsRow | null>({
        queryKey: ['admin', 'species', 'details', speciesCatalogId],
        queryFn: async () => {
            if (speciesCatalogId == null) return null;
            const { data, error } = await dbClient.rpc('admin_get_species_details', {
                p_species_catalog_id: speciesCatalogId,
            });
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            return (rows[0] as SpeciesDetailsRow) ?? null;
        },
        enabled: speciesCatalogId != null,
        staleTime: 30_000,
    });
}

export function useUpsertSpeciesDetails() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: {
            p_species_catalog_id: number;
            p_description_es?: string | null;
            p_description_en?: string | null;
            p_extended_info?: Record<string, unknown> | null;
            p_seo_title?: string | null;
            p_seo_description?: string | null;
            p_seo_faq?: { question: string; answer: string }[] | null;
        }) => {
            const { data, error } = await dbClient.rpc('admin_upsert_species_details', params);
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            const row = rows[0] as { success?: boolean; message?: string };
            if (!row?.success) throw new Error(row?.message || 'Error al guardar detalles');
            return row;
        },
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: ['admin', 'species', 'details', v.p_species_catalog_id] });
            qc.invalidateQueries({ queryKey: ['admin', 'species', 'stats', v.p_species_catalog_id] });
            qc.invalidateQueries({ queryKey: ['admin', 'species', 'list'] });
        },
    });
}

// ─── Stats, relaciones, álbum Cromos ─────────────────────────────────────────

export function useSpeciesStats(speciesCatalogId: number | null) {
    return useQuery<SpeciesStats | null>({
        queryKey: ['admin', 'species', 'stats', speciesCatalogId],
        queryFn: async () => {
            if (speciesCatalogId == null) return null;
            const { data, error } = await dbClient.rpc('admin_get_species_stats', {
                p_species_catalog_id: speciesCatalogId,
            });
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            return (rows[0] as SpeciesStats) ?? null;
        },
        enabled: speciesCatalogId != null,
        staleTime: 30_000,
    });
}

export function useSpeciesRelated(speciesCatalogId: number | null) {
    return useQuery<SpeciesRelatedEntry[]>({
        queryKey: ['admin', 'species', 'related', speciesCatalogId],
        queryFn: async () => {
            if (speciesCatalogId == null) return [];
            const { data, error } = await dbClient.rpc('admin_get_species_related', {
                p_species_catalog_id: speciesCatalogId,
            });
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data) as SpeciesRelatedEntry[];
        },
        enabled: speciesCatalogId != null,
        staleTime: 30_000,
    });
}

export function useAddSpeciesRelated() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: {
            p_species_catalog_id: number;
            p_related_species_catalog_id: number;
            p_relation_type?: SpeciesRelationType;
        }) => {
            const { data, error } = await dbClient.rpc('admin_add_species_related', params);
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            const row = rows[0] as { success?: boolean; message?: string };
            if (!row?.success) throw new Error(row?.message || 'Error al añadir relación');
            return row;
        },
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: ['admin', 'species', 'related', v.p_species_catalog_id] });
            qc.invalidateQueries({ queryKey: ['admin', 'species', 'stats', v.p_species_catalog_id] });
        },
    });
}

export function useRemoveSpeciesRelated() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: {
            p_species_catalog_id: number;
            p_related_species_catalog_id: number;
        }) => {
            const { data, error } = await dbClient.rpc('admin_remove_species_related', params);
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            const row = rows[0] as { success?: boolean; message?: string };
            if (!row?.success) throw new Error(row?.message || 'Error al eliminar relación');
            return row;
        },
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: ['admin', 'species', 'related', v.p_species_catalog_id] });
            qc.invalidateQueries({ queryKey: ['admin', 'species', 'stats', v.p_species_catalog_id] });
        },
    });
}

export function useCromosAlbumList(params: { waterType?: string | null; search?: string }) {
    const { waterType, search } = params;
    return useQuery<CromosAlbumEntry[]>({
        queryKey: ['admin', 'species', 'cromos-album', waterType, search],
        queryFn: async () => {
            const rpcParams: Record<string, unknown> = {};
            if (waterType && waterType !== 'all') rpcParams.p_water_type = waterType;
            if (search?.trim()) rpcParams.p_search = search.trim();
            const { data, error } = await dbClient.rpc('admin_get_cromos_album_species', rpcParams);
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data) as CromosAlbumEntry[];
        },
        staleTime: 30_000,
    });
}

export function useUpsertCromosAlbumSpecies() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: {
            p_scientific_name: string;
            p_display_name: string;
            p_water_type: string;
            p_rarity?: string;
        }) => {
            const { data, error } = await dbClient.rpc('admin_upsert_cromos_album_species', params);
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            const row = rows[0] as { success?: boolean; message?: string };
            if (!row?.success) throw new Error(row?.message || 'Error al guardar álbum Cromos');
            return row;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'species', 'cromos-album'] });
            qc.invalidateQueries({ queryKey: ['admin', 'species', 'stats'] });
            qc.invalidateQueries({ queryKey: ['admin', 'species', 'list'] });
        },
    });
}

export function useDeleteCromosAlbumSpecies() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: { p_scientific_name: string }) => {
            const { data, error } = await dbClient.rpc('admin_delete_cromos_album_species', params);
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            const row = rows[0] as { success?: boolean; message?: string };
            if (!row?.success) throw new Error(row?.message || 'Error al eliminar del álbum Cromos');
            return row;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'species', 'cromos-album'] });
            qc.invalidateQueries({ queryKey: ['admin', 'species', 'stats'] });
            qc.invalidateQueries({ queryKey: ['admin', 'species', 'list'] });
        },
    });
}
